import React, { useState } from 'react';
import {
  profileData,
  skills,
  education,
  journey,
  skillCategoryOrder,
  tools,
  toolCategories,
} from '../data/mock';
import { MapPin, Mail } from 'lucide-react';
import { Card, Label, Tag, Marked } from './primitives';

const toneForCategory = ['yellow', 'mint', 'blue', 'pink'];

const PREVIEW_CATEGORIES = 3;

const ToggleButton = ({ open, onClick, closedLabel, openLabel }) => (
  <button
    onClick={onClick}
    aria-expanded={open}
    className="mt-5 px-4 py-2 bg-paper text-ink text-xs font-semibold rounded-lg border-[1.5px] border-ink shadow-hard-sm hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all"
  >
    {open ? openLabel : closedLabel}
  </button>
);

const About = () => {
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const skillsByCategory = skills.reduce((acc, skill) => {
    (acc[skill.category] = acc[skill.category] || []).push(skill);
    return acc;
  }, {});

  const toolsByCategory = tools.reduce((acc, tool) => {
    (acc[tool.category] = acc[tool.category] || []).push(tool);
    return acc;
  }, {});

  return (
    <section
      id="about"
      className="bg-paper border-y-[1.5px] border-ink py-16 sm:py-24 scroll-mt-20"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Label className="mb-3">The long way round</Label>
        <h2 className="text-2xl sm:text-4xl font-bold text-ink tracking-[-0.02em]">
          About me
        </h2>

        <div className="mt-9 sm:mt-10 grid md:grid-cols-[1.5fr_1fr] gap-8 md:gap-10 items-start">
          {/* Journey */}
          <div>
            <div className="space-y-4">
              {journey.map((para, i) => (
                <p key={i} className="text-sm sm:text-[15px] text-ink-soft leading-relaxed">
                  <Marked text={para} />
                </p>
              ))}
            </div>

            {/* Education */}
            <div className="mt-10 pt-8 border-t border-rule">
              <Label as="h3" className="mb-5">
                Education
              </Label>
              <div className="divide-y divide-rule">
                {education.map((edu, i) => (
                  <div key={edu.institution} className={i === 0 ? 'pb-5' : 'py-5 last:pb-0'}>
                    <h4 className="text-[15px] font-bold text-ink leading-snug">{edu.degree}</h4>
                    <p className="text-sm text-ink-soft mt-1">{edu.institution}</p>
                    <p className="eyebrow mt-2">
                      {edu.year}
                      {edu.grade && <span> · Grade {edu.grade}</span>}
                    </p>
                    <p className="text-sm text-subtle leading-relaxed mt-2.5">{edu.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tools, sitting under education to use the column height */}
            <div id="tools" className="mt-10 pt-8 border-t border-rule scroll-mt-24">
              <Label as="h3" className="mb-1.5">
                Tools I use
              </Label>
              <p className="text-sm text-subtle">The stack I work in day to day.</p>
              <ToggleButton
                open={showTools}
                onClick={() => setShowTools(!showTools)}
                closedLabel={`Show stack (${tools.length} tools)`}
                openLabel="Hide stack"
              />
              {showTools && (
              <div className="mt-5 divide-y divide-rule">
                {toolCategories.map((category, ci) => {
                  const items = toolsByCategory[category.name];
                  if (!items) return null;

                  return (
                    <div key={category.name} className={ci === 0 ? 'pb-5' : 'py-5 last:pb-0'}>
                      <h4 className="text-[13px] font-bold text-ink">{category.name}</h4>
                      <p className="text-[13px] text-subtle mt-0.5 mb-2.5">{category.blurb}</p>
                      <div className="flex flex-wrap gap-2">
                        {items.map((tool) => (
                          <Tag key={tool.name} tone={toneForCategory[ci % toneForCategory.length]}>
                            {tool.name}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>

            {/* Quick info */}
            <div className="mt-8 pt-6 border-t border-rule flex flex-wrap gap-x-8 gap-y-3">
              <span className="flex items-center gap-2 text-sm text-subtle">
                <MapPin size={16} />
                {profileData.location}
              </span>
              <a
                href={`mailto:${profileData.email}`}
                className="flex items-center gap-2 text-sm text-subtle hover:text-ink transition-colors"
              >
                <Mail size={16} />
                {profileData.email}
              </a>
            </div>
          </div>

          {/* Skills */}
          <Card tint="cream" className="p-5 sm:p-6">
            <Label as="h3" className="mb-5 text-ink/70">
              Skills & expertise
            </Label>
            <div className="space-y-5">
              {(showAllSkills
                ? skillCategoryOrder
                : skillCategoryOrder.slice(0, PREVIEW_CATEGORIES)
              ).map(
                (category, ci) =>
                  skillsByCategory[category] && (
                    <div key={category}>
                      <p className="text-[11px] font-bold uppercase tracking-label text-subtle mb-2.5">
                        {category}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {skillsByCategory[category].map((skill) => (
                          <Tag
                            key={skill.name}
                            tone={toneForCategory[ci % toneForCategory.length]}
                          >
                            {skill.name}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )
              )}
            </div>
            <ToggleButton
              open={showAllSkills}
              onClick={() => setShowAllSkills(!showAllSkills)}
              closedLabel={`Show all skills (${skills.length})`}
              openLabel="Show fewer"
            />

            <div className="mt-6 pt-5 border-t border-ink/15">
              <p className="text-2xl font-bold text-ink">30+</p>
              <Label className="mt-0.5">Websites delivered</Label>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default About;
